import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { Label } from '@/components/ui/label';
import { GenericForm, GenericFormField, GenericSelect } from '@/styles/GenericForm';
import { StandardButton, SaveChangesButton, CancelButton } from '@/styles/StandardButtons';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { CatalogLineItems, useCatalogLines } from '@/features/cost-management/purchase-orders/components/CatalogLineItems';
import { PurchaseOrderCreateDialog } from '@/features/cost-management/purchase-orders/components/PurchaseOrderCreateDialog';
import { usePurchaseOrderFormOptions } from '@/features/cost-management/purchase-orders/usePurchaseOrderFormOptions';
import { commonVendorOf, lineFromRow, linesProblem, toLinePayload } from '@/features/cost-management/purchase-orders/lib/po-lines';
import { fetchEntityList } from '@/lib/entity-api';
import { formatMoney, lineCostCodeProblem, linesTotal, newLineKey, type LineItem } from '@/lib/line-items';
import { statusLabel } from '@/lib/status-label';
import { derivedDescription } from '@/lib/derived-description';
import { LinkedDocsSection, type LinkedDoc } from '@/components/shared/LinkedDocsSection';
import { VendorField } from '@/components/shared/VendorField';
import { uploadMaterialOrderAttachments, deleteMaterialOrderAttachment, fetchMaterialOrderLines } from './store';
import { MaterialOrderCharges, chargesProblem, type MaterialOrderChargesValue } from './MaterialOrderCharges';
import { parseCharge, purchaseOrderChargesOf } from './lib/material-order-math';

interface FileAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

interface Project {
  id: string;
  name: string;
}

export interface MaterialOrderData {
  id: string;
  name?: string | null;
  material_order_id?: string | number | null;
  project_id: string;
  /** the supplier's name: a vendor's, or the free text that was typed */
  supplier: string;
  /** legacy orders: the name typed when the supplier was "Other" */
  other_supplier?: string | null;
  /** set when the supplier is one of the company's vendors; null / absent for free text */
  vendor_id?: string | null;
  status: string;
  notes: string | null;
  tax: number;
  other_amount: number;
  total_cost: number | null;
}

/** What the form hands to the page on Save: the order's own fields, its items (only when they changed) and the files to attach. */
export interface MaterialOrderSubmit {
  name: string;
  project_id: string;
  /** the supplier's name (always) */
  supplier: string;
  /** the vendor's id when a vendor of the company was picked; null for a supplier typed as free text -- never a made-up id */
  vendor_id: string | null;
  status: string;
  notes: string;
  tax: number;
  other_amount: number;
  /** the items in the API's line shape; undefined = the order's items are not touched */
  lines?: Array<Record<string, unknown>>;
  attachmentsFiles: File[];
}

interface MaterialOrderFormProps {
  mode: 'create' | 'edit';
  initialData?: MaterialOrderData | null;
  projects: Project[];
  /** Saves the order. `close: false` is for Create PO, which saves first and keeps the form open. Rejects with the server's message. */
  onSubmit: (data: MaterialOrderSubmit, options?: { close?: boolean }) => void | Promise<any>;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  attachments?: FileAttachment[];
  onDeleteAttachment?: (id: string) => void;
  /** Something the list shows changed outside Save (a purchase order was made from this order): the page re-reads its list. */
  onRelatedDataChanged?: () => void;
}

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Approved', value: 'approved' },
  { label: 'Ordered', value: 'ordered' },
  { label: 'Received', value: 'received' },
  { label: 'Cancelled', value: 'cancelled' },
];

/** A stored number as the box shows it: blank for 0. */
const chargeText = (value: number | null | undefined): string => (Number(value) > 0 ? String(Number(value)) : '');

/** A purchase order made from this material order: all of them are shown (a rejected or voided one too), only the others count as "its purchase order". */
interface LinkedPurchaseOrder {
  doc: LinkedDoc;
  standing: boolean;
}

export function MaterialOrderForm({
  mode,
  initialData,
  projects,
  onSubmit,
  onCancel,
  onDirtyChange,
  attachments = [],
  onDeleteAttachment,
  onRelatedDataChanged,
}: MaterialOrderFormProps) {
  const { companyId, user } = useUser();
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const checkingMembers = useRef(false);
  const { selectedProject } = useProject();
  const orderId = mode === 'edit' ? initialData?.id : undefined;

  const uploadAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<FileAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadMaterialOrderAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteAttachmentFn = useCallback((id: string) => deleteMaterialOrderAttachment(id, user?.id), [user]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemoveSelectedFile,
    handleDelete: handleDeleteFileBase,
  } = useAttachmentStaging<FileAttachment>({
    entityId: initialData?.id,
    uploadFn: uploadAttachmentsFn,
    deleteFn: deleteAttachmentFn,
    initialUploadedFiles: attachments,
  });
  const handleDeleteFile = useCallback(async (attachmentId: string) => {
    const ok = await handleDeleteFileBase(attachmentId);
    if (ok) onDeleteAttachment?.(attachmentId);
  }, [handleDeleteFileBase, onDeleteAttachment]);
  const { comments, addComment, deleteComment } = useEntityComments('material_list', orderId ?? null);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -- Supplier: one of the company's vendors (its id is kept) or a name typed as free text (no vendor record, no id) ---------------------------------
  const savedSupplierName = initialData?.other_supplier || initialData?.supplier || '';
  const [supplierName, setSupplierName] = useState<string>(savedSupplierName);
  const [supplierVendorId, setSupplierVendorId] = useState<string | null>(initialData?.vendor_id ?? null);
  const initialSupplierRef = useRef(`${savedSupplierName}|${initialData?.vendor_id ?? ''}`);

  // -- Charges: Tax (%) and Other ($), the purchase order's two ----------------------------------------------------------------------------
  const [charges, setCharges] = useState<MaterialOrderChargesValue>({ tax: chargeText(initialData?.tax), other: chargeText(initialData?.other_amount) });
  const chargesKey = (value: MaterialOrderChargesValue) => `${value.tax.trim()}|${value.other.trim()}`;
  const initialChargesRef = useRef(chargesKey(charges));

  // -- Items: the same item table as a purchase order, with the catalog. `linesLoaded` is false while an existing order's items are being
  // read (or could not be): then they are never sent on save, so a failed read can never wipe them. ----------------------------------------------
  const [lines, setLines] = useState<LineItem[]>([]);
  const [linesLoaded, setLinesLoaded] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesLoadError, setLinesLoadError] = useState<string | null>(null);
  const initialLinesRef = useRef('[]');
  const serializeLines = (list: LineItem[]) => JSON.stringify(list.map(({ key: _key, ...rest }) => rest));
  const { vendors } = usePurchaseOrderFormOptions();
  const catalog = useCatalogLines({ isOpen: true, lines, setLines, vendorId: null, vendors });
  const { setOffersByItem, rememberOffersOf, defaultCostCodeId } = catalog;
  const linesChanged = () => linesLoaded && (orderId ? serializeLines(lines) !== initialLinesRef.current : lines.length > 0);
  const subtotal = linesTotal(lines);

  useEffect(() => {
    setLines([]);
    setOffersByItem(new Map());
    setLinesLoadError(null);
    initialLinesRef.current = '[]';
    if (!orderId) { setLinesLoaded(true); setLinesLoading(false); return undefined; }
    let cancelled = false;
    setLinesLoaded(false);
    setLinesLoading(true);
    fetchMaterialOrderLines(orderId)
      .then((rows) => {
        if (cancelled) return;
        const loaded = rows.map((row) => lineFromRow(row, null));
        setLines(loaded);
        initialLinesRef.current = serializeLines(loaded);
        setLinesLoaded(true);
        rememberOffersOf(loaded.map((l) => l.catalogItemId).filter((id): id is string => !!id));
      })
      .catch((failure) => {
        if (cancelled) return;
        console.error('Error loading the material order items:', failure);
        setLinesLoadError('The items of this material order could not be loaded. They stay as they are: saving does not change them.');
      })
      .finally(() => { if (!cancelled) setLinesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // -- Linked Docs: the purchase orders made from this material order (the only real link it has) ----------------------------------------------
  const [linkedOrders, setLinkedOrders] = useState<LinkedPurchaseOrder[]>([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkedError, setLinkedError] = useState<string | null>(null);
  const loadLinkedDocs = useCallback(async () => {
    if (!orderId) { setLinkedOrders([]); setLinkedError(null); return; }
    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const orders = await fetchEntityList<any>('purchase_order', { filters: { material_list_id: orderId } });
      setLinkedOrders(orders.map((po) => ({
        standing: po.status !== 'rejected' && po.status !== 'voided',
        doc: {
          id: po.id,
          type: 'Purchase Order',
          entityType: 'purchase_order',
          label: po.title ? `${po.po_number} — ${po.title}` : po.po_number,
          detail: [po.status ? statusLabel(po.status) : '', po.amount != null ? formatMoney(Number(po.amount)) : ''].filter(Boolean).join(' · '),
        },
      })));
    } catch (failure) {
      console.error('Error loading the linked documents:', failure);
      setLinkedError('The linked documents could not be loaded.');
    } finally {
      setLinkedLoading(false);
    }
  }, [orderId]);
  useEffect(() => { void loadLinkedDocs(); }, [loadLinkedDocs]);
  const hasStandingPurchaseOrder = linkedOrders.some((po) => po.standing);
  const [purchaseOrderStart, setPurchaseOrderStart] = useState<{ title: string; projectId: string; description: string; vendorId?: string; lines: LineItem[]; taxPct: string; otherAmount: string } | null>(null);

  // -- Attachments: their `file_url` is the permanent KrewUp address, nothing to sign ---------------------------------------------------------
  useEffect(() => {
    if (initialData?.id) setUploadedFiles(attachments || []);
  }, [initialData?.id, attachments, setUploadedFiles]);

  // -- The order's own fields ---------------------------------------------------------------------------------------------------------------
  const initialValues = useMemo(() => ({
    name: initialData?.name || '',
    project_id: initialData?.project_id || getDefaultProjectIdForNewRecord(selectedProject),
    supplier: initialData?.supplier || 'Menards',
  }), [initialData, selectedProject]);
  // Status sits next to Supplier (second row), so it is kept here rather than in the generic fields
  const [status, setStatus] = useState<string>(initialData?.status || 'draft');
  const initialStatusRef = useRef(status);
  const [currentFormData, setCurrentFormData] = useState<Record<string, any>>(initialValues);
  const initialFormDataRef = useRef<Record<string, any>>(initialValues);

  const fields = useMemo<GenericFormField[]>(() => [
    { name: 'name', label: 'Material Order Title', type: 'text', row: 1, placeholder: 'Enter material order title' },
    { name: 'project_id', label: 'Project', type: 'select', required: true, row: 1, options: projects.map((p) => ({ label: p.name, value: p.id })), placeholder: 'Select' },
  ], [projects]);

  const hasChanged = (): boolean => {
    const current = currentFormData;
    const initial = initialFormDataRef.current;
    const formChanged = Object.keys({ ...current, ...initial }).some((key) => String(current[key] ?? '').trim() !== String(initial[key] ?? '').trim());
    return formChanged
      || status !== initialStatusRef.current
      || `${supplierName.trim()}|${supplierVendorId ?? ''}` !== initialSupplierRef.current
      || chargesKey(charges) !== initialChargesRef.current
      || selectedFiles.length > 0
      || linesChanged();
  };

  const handleFieldChange = (name: string, value: any, updated: Record<string, any>) => {
    setCurrentFormData(updated);
    onDirtyChange?.(true);
  };

  const handleCloseAttempt = () => {
    if (hasChanged() && !window.confirm('Are you sure you want to lose all unsaved progress?')) return;
    onCancel();
  };

  const changeLines = (next: LineItem[]) => { catalog.changeLines(next); onDirtyChange?.(true); };

  /** Saves the order (and its items when they changed). `close: false` is for Create PO. Returns whether everything was saved. */
  const handleSubmit = async (data: Record<string, any>, { close = true }: { close?: boolean } = {}): Promise<boolean> => {
    if (isSubmitting || checkingMembers.current) return false;
    setError(null);
    const chargeError = chargesProblem(charges);
    if (chargeError) { setError(chargeError); return false; }
    if (!supplierName.trim()) { setError('Enter the supplier: pick one of your vendors or type a name.'); return false; }
    // a supplier that is one of the vendors must belong to the project: asked once, now that the order is being saved
    checkingMembers.current = true; // a second click while the question is open does nothing
    let accepted = false;
    try { accepted = await ensureMembers(data.project_id, [{ id: supplierVendorId || '', name: supplierName }]); } finally { checkingMembers.current = false; }
    if (!accepted) return false;

    // a saved item from before cost codes existed gets the company's default code, the one a new item starts with
    const toSave = lines.map((line) => (line.costCodeId || !defaultCostCodeId ? line : { ...line, costCodeId: defaultCostCodeId }));
    const sendLines = linesChanged();
    if (sendLines) {
      const problem = linesProblem(toSave) || lineCostCodeProblem(toSave);
      if (problem) { setError(problem); return false; }
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: String(data.name ?? '').trim(),
        project_id: data.project_id,
        supplier: supplierName.trim(),
        vendor_id: supplierVendorId,
        status,
        notes: initialData?.notes ?? '',
        tax: parseCharge(charges.tax, 100) ?? 0,
        other_amount: parseCharge(charges.other) ?? 0,
        lines: sendLines ? toLinePayload(toSave) : undefined,
        attachmentsFiles: selectedFiles,
      }, { close });
      if (sendLines) initialLinesRef.current = serializeLines(lines);
      initialChargesRef.current = chargesKey(charges);
      initialFormDataRef.current = JSON.parse(JSON.stringify(data));
      initialStatusRef.current = status;
      initialSupplierRef.current = `${supplierName.trim()}|${supplierVendorId ?? ''}`;
      onDirtyChange?.(false);
      return true;
    } catch (failure: any) {
      // the modal stays open with the user's changes intact
      setError(failure?.message || 'Failed to save material order. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // "Create PO": the Purchase Order form (the one the Purchase Orders page uses) opens with a COPY of this order's items and charges -- never the order's
  // own rows. The order is saved first (its items must exist) and the purchase order is saved already linked to it (purchase_orders.material_list_id): the
  // database allows one standing purchase order per material order, so a double click or a retry can never make a second one.
  const handleCreatePurchaseOrder = async () => {
    if (!orderId) return;
    const data = currentFormData;
    if (!data.project_id) { setError('Select a project before creating the purchase order.'); return; }
    if (hasChanged() && !(await handleSubmit(data, { close: false }))) return;
    if (lines.length === 0) { setError('Add at least one item before creating the purchase order.'); return; }
    const totals = purchaseOrderChargesOf(parseCharge(charges.tax, 100) ?? 0, parseCharge(charges.other) ?? 0);
    setPurchaseOrderStart({
      title: String(data.name ?? '').trim() || String(initialData?.material_order_id ?? ''),
      projectId: data.project_id,
      description: derivedDescription({ type: 'Material Order', number: initialData?.material_order_id == null ? undefined : String(initialData.material_order_id), title: data.name, description: initialData?.notes }),
      // the purchase order is addressed to the supplier when it is one of the company's vendors; otherwise to the vendor every item names (if it is the same one)
      vendorId: supplierVendorId ?? commonVendorOf(lines),
      lines: lines.map((line) => ({ ...line, key: newLineKey() })),
      ...totals,
    });
  };

  const cancelled = status === 'cancelled';
  const createPurchaseOrderDisabled = !orderId || isSubmitting || !linesLoaded || linkedLoading || hasStandingPurchaseOrder || cancelled;
  const createPurchaseOrderHint = !orderId
    ? 'Save the material order first'
    : hasStandingPurchaseOrder
    ? 'This material order already has a purchase order'
    : cancelled ? 'A cancelled material order cannot become a purchase order' : undefined;

  const footer = (
    <div className="flex gap-2">
      <CancelButton onClick={handleCloseAttempt} disabled={isSubmitting}>Cancel</CancelButton>
      <StandardButton type="button" customVariant="white" onClick={handleCreatePurchaseOrder} disabled={createPurchaseOrderDisabled} title={createPurchaseOrderHint}>
        Create PO
      </StandardButton>
      <SaveChangesButton disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</SaveChangesButton>
    </div>
  );

  const linkedEmptyText = linkedLoading ? 'Loading linked documents…' : linkedError ?? 'No linked documents';

  return (
    <>
      {membershipPrompt}
      <GenericForm
        title="Material Order"
        mode={mode}
        fields={fields}
        initialValues={initialValues}
        onSubmit={(data: Record<string, any>) => { void handleSubmit(data); }}
        onCancel={onCancel}
        isOpen
        additionalSections={(values) => (
          <>
            {/* Row 2: Supplier + Status share the left half of the modal (they stack on a narrow screen) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:w-1/2" data-testid="material-order-row-2">
              <div className="space-y-2 min-w-0" data-testid="material-order-supplier">
                <Label htmlFor="mo_supplier" className="font-form-label">Supplier <span className="text-red-500">*</span></Label>
                <VendorField
                  id="mo_supplier"
                  value={supplierName}
                  placeholder="Select a vendor or type a name"
                  onChange={(selection) => { setSupplierName(selection.name); setSupplierVendorId(selection.id); onDirtyChange?.(true); }}
                />
                <p className="text-xs text-gray-500" data-testid="supplier-kind">
                  {!supplierName.trim() ? '' : supplierVendorId ? 'One of your vendors' : 'Free text: not linked to a vendor'}
                </p>
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="mo_status" className="font-form-label">Status</Label>
                <GenericSelect id="mo_status" value={status} required onValueChange={(next) => { setStatus(next); onDirtyChange?.(true); }} options={STATUS_OPTIONS} placeholder="Select" />
              </div>
            </div>
            <CatalogLineItems
              catalog={catalog}
              lines={lines}
              vendorId={null}
              vendors={vendors}
              loading={linesLoading}
              loadError={linesLoadError}
              disabled={!linesLoaded}
              totalLabel="Subtotal:"
              onLinesChange={changeLines}
            />
            <MaterialOrderCharges subtotal={subtotal} value={charges} onChange={(next) => { setCharges(next); onDirtyChange?.(true); }} disabled={isSubmitting} />
            <LinkedDocsSection docs={linkedOrders.map((po) => po.doc)} emptyText={linkedEmptyText} hasUnsavedChanges={hasChanged} />
            <GenericAttachmentsSection
              projectId={values.project_id || ''}
              attachments={uploadedFiles}
              pendingFiles={selectedFiles}
              onUpload={handleFileUpload}
              onDelete={handleDeleteFile}
              onRemovePending={handleRemoveSelectedFile}
              mode={mode}
            />
            <GenericCommentsSection
              comments={comments}
              audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
              onAddComment={addComment}
              onDeleteComment={deleteComment}
              mode={mode}
              placeholder={mode === 'create' ? 'Comments are available after creating the material order' : 'Add a comment...'}
            />
          </>
        )}
        error={error}
        submitButtonText="Save Changes"
        saving={isSubmitting}
        customFooterButtons={footer}
        onFieldChange={handleFieldChange}
        hasUnsavedChanges={hasChanged}
      />
      {/* A sibling of the form (not inside it): a submit inside the purchase order form must never submit the material order */}
      <PurchaseOrderCreateDialog
        isOpen={purchaseOrderStart !== null}
        onClose={() => setPurchaseOrderStart(null)}
        onCreated={() => { setPurchaseOrderStart(null); void loadLinkedDocs(); onRelatedDataChanged?.(); }}
        defaults={purchaseOrderStart ?? undefined}
        materialListId={orderId}
      />
    </>
  );
}
